import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common credential types offered as datalist suggestions; the field is still
// free-text so any custom type works.
const TYPE_SUGGESTIONS = [
  'PT License', 'OT License', 'ST License', 'PTA License', 'COTA License',
  "Driver's License", 'Auto Insurance', 'Professional Liability Insurance',
  'TB Test', 'Skills Checklist', 'CPR Certification', 'W9', 'Contract', 'Other',
];

// Normalize a stored date (ISO or yyyy-mm-dd) into a value an <input type=date> accepts.
const toDateInput = (val) => {
  if (!val) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

/**
 * Edit a credential/document's metadata: name, type, expiration date,
 * category and (for tax docs) tax year. Fetches the latest values on open
 * and saves via PUT /documents/{id}, which also re-runs active/archived
 * reconciliation server-side.
 */
export default function EditCredentialModal({ open, documentId, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    document_name: '', document_type: '', expiration_date: '',
    category: 'credential', tax_year: '',
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open || !documentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/documents/${documentId}`, { headers });
        if (cancelled) return;
        const d = res.data || {};
        setForm({
          document_name: d.document_name || '',
          document_type: d.document_type || '',
          expiration_date: toDateInput(d.expiration_date),
          category: d.category || 'credential',
          tax_year: d.tax_year != null ? String(d.tax_year) : '',
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not load credential');
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.document_name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        document_name: form.document_name.trim(),
        document_type: form.document_type.trim(),
        category: form.category,
        expiration_date: form.category === 'tax' ? null : (form.expiration_date || null),
        tax_year: form.category === 'tax' && form.tax_year ? parseInt(form.tax_year, 10) : null,
      };
      const res = await axios.put(`${API}/documents/${documentId}`, payload, { headers });
      toast.success('Credential updated');
      onSaved?.(res.data?.document);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update credential');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="edit-credential-modal"
    >
      <div className="bg-background rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">Edit credential</h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted" data-testid="edit-credential-close">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <div>
              <Label className="text-sm">Name / Label</Label>
              <Input
                value={form.document_name}
                onChange={(e) => set('document_name', e.target.value)}
                className="mt-1"
                data-testid="edit-cred-name"
              />
            </div>

            <div>
              <Label className="text-sm">Type</Label>
              <Input
                list="cred-type-suggestions"
                value={form.document_type}
                onChange={(e) => set('document_type', e.target.value)}
                placeholder="e.g. PT License"
                className="mt-1"
                data-testid="edit-cred-type"
              />
              <datalist id="cred-type-suggestions">
                {TYPE_SUGGESTIONS.map((t) => <option key={t} value={t} />)}
              </datalist>
            </div>

            <div>
              <Label className="text-sm">Category</Label>
              <select
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                className="w-full h-9 mt-1 border rounded-md px-3 text-sm bg-background"
                data-testid="edit-cred-category"
              >
                <option value="credential">Credential</option>
                <option value="tax">Tax</option>
                <option value="other">Other</option>
              </select>
            </div>

            {form.category === 'tax' ? (
              <div>
                <Label className="text-sm">Tax Year</Label>
                <Input
                  type="number"
                  min="2000"
                  max="2100"
                  value={form.tax_year}
                  onChange={(e) => set('tax_year', e.target.value)}
                  placeholder="e.g. 2025"
                  className="mt-1"
                  data-testid="edit-cred-tax-year"
                />
              </div>
            ) : (
              <div>
                <Label className="text-sm">Expiration date</Label>
                <Input
                  type="date"
                  value={form.expiration_date}
                  onChange={(e) => set('expiration_date', e.target.value)}
                  className="mt-1"
                  data-testid="edit-cred-expiration"
                />
              </div>
            )}
          </div>
        )}

        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving || loading} data-testid="edit-cred-save">
            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
